import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { EditIdentityModal } from "../components/allowance/EditIdentity";
import { IdentityView } from "../components/allowance/IdentityView";
import { RegisterIdentity } from "../components/allowance/RegisterIdentity";
import { HolderSelect } from "../components/holders/HolderSelect";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, SkeletonRows } from "../components/ui/Feedback";
import { SearchField, SelectField } from "../components/ui/Input";
import { RowEditButton } from "../components/ui/RowEditButton";
import { Pill } from "../components/ui/Status";
import { Cell, Pagination, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import type { Column } from "../components/ui/Table";
import { Metric } from "../components/ui/Text";
import { getPage } from "../lib/api";
import { count, kwh } from "../lib/format";
import { useSession } from "../lib/session";
import type { Identity, TokenType } from "../lib/types";

/**
 * Identities — the cards and VINs an operator has registered. An unknown tag
 * presented at a station is refused, never enrolled, so registration is the only
 * way in. Each belongs to a holder, and draws on the allowance of the nearest
 * holder above it that has one.
 *
 * Usage is deliberately absent from this table. It costs a period resolution and a
 * SUM per row (spec 11 budgets this page at 200 ms), and a figure is only ever
 * read on the detail view.
 */

const COLUMNS: readonly Column[] = [
  { key: "label", label: "Identity", width: "22%" },
  { key: "tag", label: "ID tag", width: "20%" },
  { key: "holder", label: "Holder", width: "18%" },
  { key: "status", label: "Status", width: "10%" },
  { key: "policy", label: "Allowance" },
  { key: "quota", label: "Quota", numeric: true, width: "11%" },
];

/** Shown only to an operator who may edit cards; no header text, the icon is the label. */
const EDIT_COLUMN: Column = { key: "edit", label: "", width: "3rem" };

const TOKEN_TYPES: readonly { readonly value: TokenType; readonly label: string }[] = [
  { value: "rfid", label: "RFID card" },
  { value: "vin", label: "Vehicle VIN" },
];

export function Identities() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();

  const [registering, setRegistering] = useState(false);
  // The card being edited. While set, the view modal steps aside for the edit
  // one and comes back when it closes, still showing the same card.
  const [editing, setEditing] = useState<Identity | null>(null);

  // The card open in the view modal lives in the URL, so a link to it is shareable
  // and the back button closes it.
  const viewing = params.get("identity") ?? "";

  const search = params.get("search") ?? "";
  const tokenType = params.get("tokenType") ?? "";
  const holderId = params.get("holderId") ?? "";
  const status = params.get("status") ?? "";
  const withoutPolicy = params.get("withoutPolicy") === "true";
  const page = positiveInt(params.get("page")) ?? 1;

  const identities = useQuery({
    queryKey: ["identities", { search, tokenType, holderId, status, withoutPolicy, page }],
    queryFn: () =>
      getPage<Identity>("/identities", {
        query: {
          search,
          tokenType,
          holderId,
          status,
          // Omitted rather than sent as `false`, which is what the route's
          // `=== "true"` check expects and what keeps the URL free of no-op params.
          withoutPolicy: withoutPolicy || undefined,
          page,
        },
      }),
    placeholderData: (previous) => previous,
  });

  const rows = identities.data?.rows ?? [];
  const canEdit = can("identities.update");
  const columns = canEdit ? [...COLUMNS, EDIT_COLUMN] : COLUMNS;

  function setViewing(identityId: string) {
    const next = new URLSearchParams(params);
    if (identityId === "") next.delete("identity");
    else next.set("identity", identityId);
    setParams(next, { replace: identityId === "" });
  }

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === "" || value === "false") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        eyebrow="Allowances"
        title="Identities"
        metrics={
          <>
            <Metric
              label="Total"
              value={count(identities.data?.meta?.total)}
              hint="matching filters"
            />
            <Metric
              label="On this page"
              value={count(rows.filter((row) => row.allowance === null).length)}
              hint="no allowance"
            />
          </>
        }
        lede="Allowances are set on holders and shared by everything under them. A card with none is tracked but never flagged — a deliberate state, not a gap."
        {...(can("identities.create") && !registering
          ? {
              actions: (
                <Button variant="primary" onClick={() => setRegistering(true)}>
                  Register a card
                </Button>
              ),
            }
          : {})}
      />

      {registering && can("identities.create") ? (
        <RegisterIdentity onClose={() => setRegistering(false)} />
      ) : null}

      <form
        onSubmit={(event) => event.preventDefault()}
        className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-3 xl:grid-cols-5"
      >
        <SearchField
          label="Search"
          placeholder="Label or ID tag"
          value={search}
          onChange={(event) => setFilter("search", event.target.value)}
        />
        <SelectField
          label="Token"
          value={tokenType}
          onChange={(event) => setFilter("tokenType", event.target.value)}
        >
          <option value="">Any</option>
          {TOKEN_TYPES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectField>
        <HolderSelect
          label="Holder"
          value={holderId}
          emptyLabel="Any"
          onChange={(value) => setFilter("holderId", value)}
          hint="Held directly. A group's subgroups have their own entries."
        />
        <SelectField
          label="Status"
          value={status}
          onChange={(event) => setFilter("status", event.target.value)}
        >
          {/* Only two values exist — `identity_status_check` permits Accepted and
              Blocked and nothing else. Offering Expired or Invalid would be a
              filter that can never match a row. */}
          <option value="">Any</option>
          <option value="Accepted">Accepted</option>
          <option value="Blocked">Blocked</option>
        </SelectField>
        <SelectField
          label="Allowance"
          value={withoutPolicy ? "true" : ""}
          onChange={(event) => setFilter("withoutPolicy", event.target.value)}
        >
          <option value="">Any</option>
          <option value="true">No allowance governs it</option>
        </SelectField>
      </form>

      {identities.isError ? (
        <ErrorNotice error={identities.error} onRetry={() => void identities.refetch()} />
      ) : null}

      <div className="flex flex-col gap-3">
        <TableFrame>
          <TableHead columns={columns} />
          {identities.isPending ? (
            <SkeletonRows rows={8} columns={columns.length} />
          ) : (
            <tbody>
              {rows.map((row, index) => (
                <TableRow key={row.id} index={index} onActivate={() => setViewing(row.id)}>
                  <Cell>
                    <span className="font-medium text-ink">{row.label}</span>
                  </Cell>
                  <Cell muted>
                    <div className="flex flex-col gap-0.5">
                      <span className="font-mono">{row.idTag}</span>
                      <span className="text-micro tracking-normal normal-case text-ink-faint">
                        {row.tokenType === "vin" ? "VIN" : "RFID"}
                      </span>
                    </div>
                  </Cell>
                  <Cell>{row.holder.name}</Cell>
                  <Cell>
                    <Pill tone={row.status === "Accepted" ? "available" : "faulted"}>
                      {row.status}
                    </Pill>
                  </Cell>
                  <Cell muted={row.allowance === null}>
                    {row.allowance === null ? (
                      "None"
                    ) : (
                      <div className="flex flex-col gap-0.5">
                        <span>{row.allowance.policy.name}</span>
                        {/* Whose pool, when it is not the card's own holder's: the
                            figure on the detail page is shared with every card
                            under that holder. */}
                        {row.allowance.holder.id === row.holder.id ? null : (
                          <span className="text-micro tracking-normal normal-case text-ink-faint">
                            via {row.allowance.holder.name}
                          </span>
                        )}
                      </div>
                    )}
                  </Cell>
                  <Cell numeric muted={row.allowance === null}>
                    {row.allowance === null ? "—" : `${kwh(row.allowance.policy.quotaWh)} kWh`}
                  </Cell>
                  {canEdit ? (
                    <Cell numeric>
                      <RowEditButton label={row.label} onEdit={() => setEditing(row)} />
                    </Cell>
                  ) : null}
                </TableRow>
              ))}
            </tbody>
          )}
        </TableFrame>

        {!identities.isPending && rows.length === 0 ? (
          <EmptyState
            title="No identities match these filters."
            detail="Cards appear here once an operator registers them. A card a station has never been told about is refused."
          />
        ) : null}

        <Pagination
          meta={identities.data?.meta}
          shown={rows.length}
          onPage={(next) => {
            const nextParams = new URLSearchParams(params);
            if (next <= 1) nextParams.delete("page");
            else nextParams.set("page", String(next));
            setParams(nextParams);
          }}
        />
      </div>

      <IdentityView
        identityId={viewing}
        open={viewing !== "" && editing === null}
        onOpenChange={(open) => {
          if (!open) setViewing("");
        }}
        onEdit={setEditing}
      />

      {editing === null ? null : (
        <EditIdentityModal
          identity={editing}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          onSaved={() => setEditing(null)}
        />
      )}
    </>
  );
}

function positiveInt(raw: string | null): number | undefined {
  if (raw === null || !/^\d+$/.test(raw)) return undefined;
  const value = Number(raw);
  return value >= 1 ? value : undefined;
}
